/**
 * useRideTracking Hook - Real-time ride tracking with WebSocket
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import wsService from '../services/websocket';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';

// Get backend URL - handles web, PWA, and native Capacitor
const getBackendUrl = () => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  if (Capacitor.isNativePlatform()) {
    return 'https://bizilgo.com';
  }
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol;
    if (protocol === 'capacitor:' || protocol === 'file:' || protocol === 'ionic:') {
      return 'https://bizilgo.com';
    }
    if (protocol === 'https:' || protocol === 'http:') {
      return window.location.origin;
    }
  }
  return 'https://bizilgo.com';
};

const API = getBackendUrl() + '/api';

/**
 * Hook for tracking a ride in real-time
 * @param {string} rideId - ID of the ride to track
 * @param {string} userId - Current user's ID
 * @param {boolean} autoConnect - Whether to auto-connect WebSocket
 */
export const useRideTracking = (rideId, userId, autoConnect = true) => {
  const [driverLocation, setDriverLocation] = useState(null);
  const [isRealtime, setIsRealtime] = useState(false);
  const [isDriverConnected, setIsDriverConnected] = useState(false);
  const [rideStatus, setRideStatus] = useState(null);
  const [driverInfo, setDriverInfo] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  
  const pollIntervalRef = useRef(null);
  const lastLocationRef = useRef(null);

  // Connect to WebSocket
  useEffect(() => {
    if (autoConnect && userId) {
      wsService.connect(userId);
      
      const unsubConnect = wsService.on('connected', () => {
        setIsConnected(true);
        if (rideId) {
          wsService.subscribeToRide(rideId);
        }
      });

      const unsubDisconnect = wsService.on('disconnected', () => {
        setIsConnected(false);
        setIsRealtime(false);
      });

      return () => {
        unsubConnect();
        unsubDisconnect();
      };
    }
  }, [userId, autoConnect, rideId]);

  // Subscribe to ride when rideId changes
  useEffect(() => {
    if (rideId && isConnected) {
      wsService.subscribeToRide(rideId);
      
      return () => {
        wsService.unsubscribeFromRide(rideId);
      };
    }
  }, [rideId, isConnected]);

  // Handle driver location updates
  useEffect(() => {
    if (!rideId) return;

    const handleLocationUpdate = (data) => {
      if (data.ride_id === rideId && data.location) {
        setDriverLocation(data.location);
        setIsRealtime(true);
        lastLocationRef.current = data.location;
      }
    };

    const handleRideStatus = (data) => {
      if (data.ride_id === rideId) {
        setRideStatus(data.status);
        if (data.ride) {
          setDriverInfo({
            name: data.ride.driver_name,
            phone: data.ride.driver_phone,
            vehicle: data.ride.driver_vehicle,
            rating: data.ride.driver_rating
          });
        }
      }
    };

    const unsubLocation = wsService.on('driver_location_update', handleLocationUpdate);
    const unsubStatus = wsService.on('ride_status_update', handleRideStatus);
    const unsubAccepted = wsService.on('ride_accepted', handleRideStatus);

    return () => {
      unsubLocation();
      unsubStatus();
      unsubAccepted();
    };
  }, [rideId]);

  // Fallback polling for location when WebSocket isn't providing updates
  useEffect(() => {
    if (!rideId) return;

    const pollLocation = async () => {
      try {
        const response = await axios.get(`${API}/rides/${rideId}/driver-location`);
        const data = response.data;
        
        if (data.driver_location) {
          // Only update if different from WebSocket data
          if (!lastLocationRef.current || 
              lastLocationRef.current.lat !== data.driver_location.lat ||
              lastLocationRef.current.lng !== data.driver_location.lng) {
            setDriverLocation(data.driver_location);
            lastLocationRef.current = data.driver_location;
          }
        }
        
        setIsRealtime(data.is_realtime || false);
        setIsDriverConnected(data.driver_connected || false);
        setRideStatus(data.status);
        
        if (data.driver_name) {
          setDriverInfo(prev => ({
            ...prev,
            name: data.driver_name
          }));
        }
        
        setError(null);
      } catch (err) {
        if (err.response?.status !== 404) {
          setError(err.message);
        }
      }
    };

    // Initial poll
    pollLocation();

    // Set up polling interval (every 5 seconds for fallback)
    pollIntervalRef.current = setInterval(pollLocation, 5000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [rideId]);

  // Manually request driver location
  const refreshLocation = useCallback(() => {
    if (rideId && isConnected) {
      wsService.requestDriverLocation(rideId);
    }
  }, [rideId, isConnected]);

  return {
    driverLocation,
    isRealtime,
    isDriverConnected,
    rideStatus,
    driverInfo,
    isConnected,
    error,
    refreshLocation
  };
};

/**
 * Hook for WebSocket connection status
 * @param {string} userId - User ID
 */
export const useWebSocket = (userId) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);

  useEffect(() => {
    if (userId) {
      wsService.connect(userId);

      const unsubConnect = wsService.on('connected', () => setIsConnected(true));
      const unsubDisconnect = wsService.on('disconnected', () => setIsConnected(false));
      const unsubMessage = wsService.on('message', (data) => setLastMessage(data));

      // Set up ping interval to keep connection alive
      const pingInterval = setInterval(() => {
        if (wsService.getConnectionStatus()) {
          wsService.ping();
        }
      }, 30000);

      return () => {
        unsubConnect();
        unsubDisconnect();
        unsubMessage();
        clearInterval(pingInterval);
        wsService.disconnect();
      };
    }
  }, [userId]);

  const sendMessage = useCallback((data) => {
    wsService.send(data);
  }, []);

  return {
    isConnected,
    lastMessage,
    sendMessage,
    subscribe: wsService.on.bind(wsService)
  };
};

export default useRideTracking;
